import React, { useCallback, useLayoutEffect, useState, useMemo } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import PropTypes from "prop-types";
import debounce from "lodash/debounce";
import classNames from "classnames";

/**
 * Enhanced SearchBox component with uniform sizing and styling
 *
 * @param {Object} props - Component props
 * @param {Object} props.query - Current query object containing searchTerm
 * @param {Function} props.setQuery - Function to update query state
 * @param {string} [props.placeholder="Search..."] - Input placeholder text
 * @param {string} [props.size="md"] - Size variant (sm, md, lg)
 * @param {string} [props.variant="default"] - Style variant (default, outlined, minimal)
 * @param {string} [props.className] - Additional CSS classes for container
 * @param {string} [props.inputClassName] - Additional CSS classes for input
 * @param {boolean} [props.disabled=false] - Whether the input is disabled
 * @param {number} [props.debounceMs=300] - Debounce delay in milliseconds
 */
function EnhancedSearchBox({
  query,
  setQuery,
  placeholder = "Search...",
  size = "md",
  variant = "default",
  className = "",
  inputClassName = "",
  disabled = false,
  debounceMs = 300,
}) {
  const [searchBox, setSearchBox] = useState("");

  useLayoutEffect(() => {
    queueMicrotask(() => setSearchBox(query.searchTerm || ""));
  }, [query.searchTerm]);

  const debouncedSearch = useMemo(
    () =>
      debounce(val => {
        setQuery(prev => ({
          ...prev,
          searchTerm: val,
          page: 1,
        }));
      }, debounceMs),
    [setQuery, debounceMs],
  );

  const handleChange = useCallback(
    e => {
      setSearchBox(e.target.value);
      if (!disabled) {
        debouncedSearch(e.target.value);
      }
    },
    [debouncedSearch, disabled],
  );

  const handleKeyDown = event => {
    if (event.keyCode === 13) {
      event.preventDefault();
    }
  };

  // Size variants
  const sizeClasses = {
    sm: {
      container: "h-8",
      input: "h-8 text-sm pl-8 pr-3",
      icon: "w-4 h-4 left-2",
    },
    md: {
      container: "h-10",
      input: "h-10 text-sm pl-9 pr-3",
      icon: "w-4 h-4 left-3",
    },
    lg: {
      container: "h-12",
      input: "h-12 text-base pl-10 pr-4",
      icon: "w-5 h-5 left-3",
    },
  };

  // Variant styles
  const variantClasses = {
    default: "border-gray-300 focus:ring-indigo-500 focus:border-indigo-500",
    outlined: "border-2 border-gray-300 focus:ring-indigo-500 focus:border-indigo-500",
    minimal: "border-0 border-b-2 border-gray-200 focus:ring-0 focus:border-indigo-500 rounded-none",
  };

  const currentSize = sizeClasses[size];
  const currentVariant = variantClasses[variant];

  return (
    <div className={classNames("relative", currentSize.container, className)}>
      <label htmlFor="search" className="sr-only">
        Search
      </label>
      <div className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center">
        <MagnifyingGlassIcon className={classNames("text-gray-400", currentSize.icon)} aria-hidden="true" />
      </div>
      <input
        type="text"
        name="search"
        id="search"
        value={searchBox}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        disabled={disabled}
        className={classNames(
          "block w-full rounded-md shadow-sm transition-colors duration-200",
          "bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100",
          "placeholder-gray-500 dark:placeholder-slate-400",
          currentSize.input,
          currentVariant,
          disabled && "opacity-50 cursor-not-allowed bg-gray-50 dark:bg-slate-900",
          inputClassName,
        )}
        placeholder={placeholder}
      />
    </div>
  );
}

EnhancedSearchBox.propTypes = {
  query: PropTypes.object.isRequired,
  setQuery: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  variant: PropTypes.oneOf(["default", "outlined", "minimal"]),
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  disabled: PropTypes.bool,
  debounceMs: PropTypes.number,
};

export default EnhancedSearchBox;
